import React from "react";

/**
 * Media section for species form
 * Handles image and audio file management
 * 
 * @param {Array} specieImageData - Existing images [{url, filename, image_id, species_id}]
 * @param {Function} handleDeleteImage - Handler to delete an image
 * @param {Function} openAddImageModal - Handler to open image modal
 * @param {Object} specieSound - Current sound data {id, file_path, specie_id}
 * @param {Function} openAddAudioModal - Handler to open audio modal
 * @param {Function} handleDeleteSound - Handler to delete sound
 */
export default function MediaSection({
    specieImageData,
    handleDeleteImage,
    openAddImageModal,
    specieSound,
    openAddAudioModal,
    handleDeleteSound
}) {
    return (
        <>
            {/* Current Images Section */}
            {specieImageData && specieImageData.length > 0 ? (
                <div className="pt-4 border-t border-gray-200">
                    <h3 className="font-semibold text-charcoal mb-3">Nuvarande bilder</h3>
                    <div className="grid grid-cols-2 gap-3 mb-3">
                        {specieImageData.map((imageData, index) => (
                            <div key={index} className="relative group">
                                <img
                                    src={imageData.url}
                                    alt={`Art bild ${index + 1}`}
                                    className="w-full h-32 object-cover rounded-lg border border-gray-200"
                                />
                                <button
                                    onClick={() => handleDeleteImage(imageData)}
                                    className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white p-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                                    title="Ta bort bild"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                </button>
                            </div>
                        ))}
                    </div>
                    <button
                        onClick={openAddImageModal}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 px-4 rounded-lg transition-colors duration-300 flex items-center justify-center"
                    >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        Ändra / Lägg till bild
                    </button>
                </div>
            ) : (
                <div className="pt-4">
                    <button
                        onClick={openAddImageModal}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-300 flex items-center justify-center"
                    >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        Lägg till Bild
                    </button>
                </div>
            )}

            {/* Current Sound Section */}
            {specieSound?.file_path ? (
                <div className="pt-4 border-t border-gray-200">
                    <h3 className="font-semibold text-charcoal mb-3">Nuvarande ljudspår</h3>
                    <div className="bg-purple-50 rounded-lg p-3 mb-3">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="bg-purple-600 rounded-full p-2">
                                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                                </svg>
                            </div>
                            <span className="text-sm text-purple-800 font-medium truncate flex-1">
                                {specieSound.file_path}
                            </span>
                        </div>
                        <audio 
                            controls
                            preload="none" 
                            className="w-full h-10"
                            src={`${process.env.REACT_APP_API_URL}/storage/uploads/species/${specieSound.species_id}/sounds/${specieSound.file_path}`}
                        >
                            Din webbläsare stödjer inte ljuduppspelning.
                        </audio>
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={openAddAudioModal}
                            className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2.5 px-4 rounded-lg transition-colors duration-300 flex items-center justify-center"
                        >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                            </svg>
                            Ändra ljudspår
                        </button>
                        <button
                            onClick={handleDeleteSound}
                            className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2.5 px-4 rounded-lg transition-colors duration-300 flex items-center justify-center"
                            title="Ta bort ljudspår"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                        </button>
                    </div>
                </div>
            ) : (
                <div className="pt-4">
                    <button
                        onClick={openAddAudioModal}
                        className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-300 flex items-center justify-center"
                    >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                        </svg>
                        Lägg till Ljudfil
                    </button>
                </div>
            )}
        </>
    );
}
